import { useMemo } from 'react';

import { ContextVariableFilterItemValue } from 'src/components/global-filter-drawer/types';
import { getResolutionTierFilterOptions } from 'src/pages/intent-conversation-analytics/constants';
import { EventChartProperty } from 'src/services/insight-cards/types';

export const useGetFilterCount = ({
  channels,
  cvFilters,
  eventFilters,
  isHumanFeedbackFiltered = false,
  multiSelected,
  workflowTags,
}: {
  channels: string[];
  cvFilters: ContextVariableFilterItemValue[];
  eventFilters: EventChartProperty[];
  isHumanFeedbackFiltered?: boolean;
  multiSelected: string[];
  workflowTags: string[];
}) => {
  const filterCount = useMemo(() => {
    /**
     * Business logic for how to count filters
     * If all options are selected filter is not counted
     * If no options are selected filter is not counted
     * Only count filters once per category (Date, channel, chat id, etc)
     * Others - each parent option is a category
     */
    const workflowTypesCount =
      multiSelected.filter(filter => filter.includes('autonomous_agent_used'))
        .length === 1
        ? 1
        : 0;
    const workflowTagsOptionsCount = workflowTags.length + 1;
    const workflowTagsFilter = multiSelected.filter(filter =>
      filter.includes('workflow_tags'),
    ).length;
    const workflowTagsCount =
      workflowTagsFilter === workflowTagsOptionsCount ||
      workflowTagsFilter === 0
        ? 0
        : 1;
    const workflowIdsCount =
      multiSelected.filter(filter => filter.startsWith('workflow_id.')).length >
      0
        ? 1
        : 0;
    const intentIdsCount =
      multiSelected.filter(filter => filter.includes('intent_id')).length > 0
        ? 1
        : 0;
    const csatRatingCount = multiSelected.filter(filter =>
      filter.includes('csat_ratings'),
    ).length;
    const csatCount = csatRatingCount === 0 || csatRatingCount === 6 ? 0 : 1;
    const queryExistFilter = multiSelected.filter(filter =>
      filter.includes('query_exists'),
    );
    const queryExistCount =
      queryExistFilter.length === 0 || queryExistFilter.length === 2 ? 0 : 1;
    const workflowVersionsCount =
      multiSelected.filter(filter => filter.startsWith('workflow_versions.'))
        .length > 0
        ? 1
        : 0;
    const cvFiltersCount = cvFilters.length;
    const eventFiltersCount = eventFilters.length;
    const channelsCount =
      channels.length === 3 || channels.length === 0 ? 0 : 1;
    // startsWith so the `conversation_types.deflection` token doesn't also
    // count as a Deflected filter.
    const deflectionsCount =
      multiSelected.filter(filter => filter.startsWith('deflection.')).length >
      0
        ? 1
        : 0;
    const resolutionAutomatedFilterCount = multiSelected.filter(filter =>
      filter.startsWith('resolution_automated.'),
    ).length;
    const resolutionAutomatedOptionCount =
      getResolutionTierFilterOptions().find(
        filter => filter.value === 'resolution_automated',
      )?.options.length ?? 0;
    const resolutionAutomatedCount =
      resolutionAutomatedFilterCount === 0 ||
      resolutionAutomatedFilterCount === resolutionAutomatedOptionCount
        ? 0
        : 1;
    const resolutionTierFilterCount = multiSelected.filter(filter =>
      filter.startsWith('resolution_tiers.'),
    ).length;
    const resolutionTierOptionCount =
      getResolutionTierFilterOptions().find(
        filter => filter.value === 'resolution_tiers',
      )?.options.length ?? 0;
    const resolutionTierCount =
      resolutionTierFilterCount === 0 ||
      resolutionTierFilterCount === resolutionTierOptionCount
        ? 0
        : 1;
    const eligibleCount =
      multiSelected.filter(filter => filter.includes('eligible')).length > 0
        ? 1
        : 0;
    const emailResponseSentCount =
      multiSelected.filter(filter => filter.includes('email_sent')).length > 0
        ? 1
        : 0;
    const emailUnansweredCauseCount =
      multiSelected.filter(
        filter =>
          filter.includes('skip_reason') || filter.includes('unanswered_cause'),
      ).length > 0
        ? 1
        : 0;
    const emailFeedbackCount =
      multiSelected.filter(filter => filter.includes('feedback')).length > 0
        ? 1
        : 0;
    // Email conversation-type tokens are `conversation_type.<value>`; the
    // startsWith match keeps them from also counting the plural
    // `conversation_types.` tokens below (and vice versa).
    const emailConversationTypesCount =
      multiSelected.filter(filter => filter.startsWith('conversation_type.'))
        .length > 0
        ? 1
        : 0;
    // Deflection vs. Proactive Conversation. Two options: selecting both (or
    // none) is no filter, so only a single selection counts.
    const conversationTypesCount =
      multiSelected.filter(filter => filter.startsWith('conversation_types.'))
        .length === 1
        ? 1
        : 0;
    // The Human feedback drawer group is its own category. It lives outside
    // multiSelected (shared with the Flagged toggle via chats_human_feedback),
    // so count it once whenever a strict, non-empty subset is selected
    // (all-or-none = no filter).
    const humanFeedbackCount = isHumanFeedbackFiltered ? 1 : 0;
    return (
      workflowTypesCount +
      workflowTagsCount +
      workflowIdsCount +
      workflowVersionsCount +
      csatCount +
      queryExistCount +
      channelsCount +
      cvFiltersCount +
      eventFiltersCount +
      deflectionsCount +
      eligibleCount +
      emailResponseSentCount +
      emailUnansweredCauseCount +
      emailFeedbackCount +
      emailConversationTypesCount +
      conversationTypesCount +
      intentIdsCount +
      humanFeedbackCount +
      resolutionAutomatedCount +
      resolutionTierCount
    );
  }, [
    workflowTags,
    channels,
    multiSelected,
    cvFilters,
    eventFilters,
    isHumanFeedbackFiltered,
  ]);

  return filterCount;
};
